import { Dispatch, FormEvent, SetStateAction, useCallback, useEffect, useState } from 'react'

import { useRouter } from 'next/navigation'

import type { Country } from '@types'

import Select from '@components/inputs/locale-selector/select'
import useLocale from '@hooks/client/useLocale'
import { setLocalFlagValue } from '@lib/helpers/client'

import World from '@images/layout/world.svg'

import styles from './locale-selector.module.scss'

type SelectorProps = {
  callback?: Dispatch<SetStateAction<boolean>>
  text?: string
  menuTop?: boolean
  display?: 'flex' | 'block'
  className?: string
}

const countries: { key: Country; name: string }[] = [
  { key: 'us', name: 'United States' },
  { key: 'uk', name: 'United Kingdom' },
]

export default function LocaleSelector({
  callback,
  text = 'Where are you looking for a job?',
  menuTop,
  display = 'flex',
  className,
}: SelectorProps): JSX.Element {
  const locale = useLocale()
  const [selected, setSelected] = useState<Country>((locale ?? 'us') as Country)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const router = useRouter()

  const submit = useCallback(
    (e?: FormEvent<HTMLFormElement>): void => {
      e?.preventDefault()
      setIsLoading(true)

      setLocalFlagValue('countrySelected', selected)
      if (locale !== selected) {
        router.push(`/${selected}`)
        callback?.(false)
        setIsLoading(false)
      }
    },
    [selected, callback, locale, router],
  )

  // Submit on change
  useEffect(() => {
    if (selected !== locale) {
      submit()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submit])

  return (
    <form
      onSubmit={submit}
      className={`${styles.form} ${className ?? className}`}
      style={{ display }}
      autoComplete="off"
    >
      <h3 className={styles[display]}>{text}</h3>
      <div
        className={`
        ${styles['input-and-btn']}
        ${styles[display]}
      `}
      >
        <Select
          initialValue={selected}
          options={countries}
          className={styles.selector}
          icon={World}
          onChange={setSelected}
          menuTop={menuTop}
          isLoading={isLoading}
        />
      </div>
    </form>
  )
}
